let __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (let s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i]
            for (const p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p]
        }
        return t
    };
    return __assign.apply(this, arguments)
}

const __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (let i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i]
    return to
}

Object.defineProperty(exports, "__esModule", { value: true })
exports.RelativesChart = void 0

const ancestor_chart_1 = require("./ancestor-chart")
const id_generator_1 = require("./id-generator")
const descendant_chart_1 = require("./descendant-chart")
const d3_array_1 = require("d3-array")
const chart_util_1 = require("./chart-util")


/** A view of a family that hides one child individual. */
const FilterChildFam = (function () {

    function FilterChildFam(fam, childId) {
        this.fam = fam
        this.childId = childId
    }

    FilterChildFam.prototype.getId = function () {
        return this.fam.getId()
    }

    FilterChildFam.prototype.getFather = function () {
        return this.fam.getFather()
    }

    FilterChildFam.prototype.getMother = function () {
        return this.fam.getMother()
    }

    FilterChildFam.prototype.getChildren = function () {
        const children = __spreadArray([], this.fam.getChildren())
        const index = children.indexOf(this.childId)
        if (index !== -1) {
            children.splice(index, 1)
        }
        return children
    }

    return FilterChildFam
}())

/** Data provider proxy that filters out a specific child individual. */
const FilterChildData = (function () {

    function FilterChildData(data, childId) {
        this.data = data
        this.childId = childId
    }

    FilterChildData.prototype.getIndi = function (id) {
        return this.data.getIndi(id)
    }

    FilterChildData.prototype.getFam = function (id) {
        return new FilterChildFam(this.data.getFam(id), this.childId)
    }

    return FilterChildData
}())

/** Chart layout showing all relatives of a person. */
const RelativesChart = (function () {

    function RelativesChart(options) {
        this.options = options
        this.util = new chart_util_1.ChartUtil(options)
        this.options.idGenerator = this.options.idGenerator || new id_generator_1.IdGenerator()
    }

    RelativesChart.prototype.layOutAncestorDescendants = function (ancestorsRoot, focusedNode) {
        const _this = this
        const ancestorData = new Map()
        ancestorsRoot.eachAfter(function (node) {
            if (!node.parent) return
            const descendantOptions = __assign({}, _this.options)
            descendantOptions.startFam = node.data.family.id
            descendantOptions.startIndi = undefined
            const child = node.id === node.parent.data.spouseParentNodeId ? node.parent.data.spouse.id : node.parent.data.indi.id
            descendantOptions.data = new FilterChildData(descendantOptions.data, child)
            descendantOptions.baseGeneration = (_this.options.baseGeneration || 0) - node.depth
            const descendantNodes = descendant_chart_1.layOutDescendants(descendantOptions)
            // The id could be modified because of duplicates. This can happen when drawing one family in multiple places of the chart).
            node.data.id = descendantNodes[0].id
            const chartInfo = chart_util_1.getChartInfoWithoutMargin(descendantNodes)
            const parentData = (node.children || []).map(function (childNode) {
                return ancestorData.get(childNode.data.id)
            })
            const parentHeight = parentData
                .map(function (data) { return data.height })
                .reduce(function (a, b) { return a + b + chart_util_1.V_SPACING }, 0)
            const data = {
                descendantNodes: descendantNodes,
                width: chartInfo.size[0],
                height: chartInfo.size[1] + parentHeight,
                x: chartInfo.origin[0],
                y: chartInfo.origin[1] + parentHeight,
            }
            ancestorData.set(node.data.id, data)
        })
        ancestorsRoot.each(function (node) {
            if (!node.parent) return
            const data = ancestorData.get(node.data.id)
            const parentData = ancestorData.get(node.parent.data.id)
            data.left = parentData && !parentData.middle ? parentData.left : node.parent.data.indiParentNodeId === node.id
            data.middle = (!parentData || parentData.middle) && node.parent.children.length === 1
        })
        ancestorsRoot.each(function (node) {
            const data = ancestorData.get(node.data.id)
            const thisNode = data ? data.descendantNodes[0] : focusedNode
            (node.children || []).forEach(function (child) {
                const childNode = ancestorData.get(child.data.id).descendantNodes[0]
                childNode.parent = thisNode
            })
            if (node.data.indiParentNodeId && node.children)
                thisNode.data.indiParentNodeId = node.children.find(function (childNode) { return childNode.id === node.data.indiParentNodeId }).data.id
            if (node.data.spouseParentNodeId && node.children)
                thisNode.data.spouseParentNodeId = node.children.find(function (childNode) { return childNode.id === node.data.spouseParentNodeId }).data.id
        })
        ancestorsRoot.each(function (node) {
            let middleX
            let parentData
            let parentNode
            let data
            const nodeData = ancestorData.get(node.data.id)
            // Lay out the nodes produced by laying out descendants of ancestors instead of the ancestor nodes from ancestorsRoot.
            const thisNode = nodeData ? nodeData.descendantNodes[0] : focusedNode
            const indiParent = node.children && node.children.find(function (child) { return child.id === node.data.indiParentNodeId })
            const spouseParent = node.children && node.children.find(function (child) { return child.id === node.data.spouseParentNodeId })
            const nodeX = thisNode.x
            const nodeY = thisNode.y
            const nodeWidth = thisNode.data.width
            const nodeHeight = thisNode.data.height
            const indiWidth = thisNode.data.indi ? thisNode.data.indi.width : 0
            const spouseWidth = thisNode.data.spouse ? thisNode.data.spouse.width : 0
            // Lay out the individual's ancestors and their descendants
            if (indiParent) {
                data = ancestorData.get(indiParent.data.id)
                parentNode = data.descendantNodes[0]
                parentData = parentNode.data
                const spouseTreeHeight = spouseParent
                    ? ancestorData.get(spouseParent.data.id).height + chart_util_1.V_SPACING
                    : 0
                const dx_1 = nodeX +
                    data.x -
                    nodeWidth / 2 +
                    indiWidth / 2 +
                    (data.left ? -data.width - chart_util_1.H_SPACING : chart_util_1.H_SPACING)
                const dy_1 = nodeY +
                    data.y -
                    nodeHeight / 2 -
                    data.height +
                    (data.left ? -chart_util_1.V_SPACING : -spouseTreeHeight - chart_util_1.V_SPACING)
                // Move all nodes by (dx, dy). The ancestor node, ie: data.descendantNodes[0] is now at (0, 0)
                data.descendantNodes.forEach(function (node) {
                    node.x += dx_1
                    node.y += dy_1
                })
                // Set the ancestor's horizontal position independently
                middleX = indiWidth / 2 -
                    nodeWidth / 2 +
                    parentData.width / 2 -
                    (parentData.indi ? parentData.indi.width : parentData.spouse.width)
                if (data.middle)
                    parentNode.x = 0
                else if (!nodeData || nodeData.middle)
                    parentNode.x = -nodeWidth / 2 - parentData.width / 2 + indiWidth - chart_util_1.H_SPACING / 2
                else if (data.left)
                    parentNode.x = nodeX + d3_array_1.min([
                        nodeWidth / 2 -
                        parentData.width / 2 -
                        spouseWidth / 2 -
                        chart_util_1.H_SPACING,
                        middleX,
                    ])
                else
                    parentNode.x = nodeX + d3_array_1.max([parentData.width / 2 - nodeWidth / 2, middleX])
            }
            // Lay out the spouse's ancestors and their descendants
            if (spouseParent) {
                data = ancestorData.get(spouseParent.data.id)
                parentNode = data.descendantNodes[0]
                parentData = parentNode.data
                const indiTreeHeight = indiParent
                    ? ancestorData.get(indiParent.data.id).height + chart_util_1.V_SPACING
                    : 0
                const dx_2 = nodeX +
                    data.x +
                    nodeWidth / 2 -
                    spouseWidth / 2 +
                    (data.left ? -data.width - chart_util_1.H_SPACING : chart_util_1.H_SPACING)
                const dy_2 = nodeY +
                    data.y -
                    nodeHeight / 2 -
                    data.height +
                    (data.left ? -indiTreeHeight - chart_util_1.V_SPACING : -chart_util_1.V_SPACING)
                // Move all nodes by (dx, dy). The ancestor node, ie: data.descendantNodes[0] is now at (0, 0)
                data.descendantNodes.forEach(function (node) {
                    node.x += dx_2
                    node.y += dy_2
                })
                // Set the ancestor's horizontal position independently
                middleX = nodeWidth / 2 - spouseWidth / 2 + parentData.width / 2 - (parentData.indi ? parentData.indi.width : parentData.spouse.width)
                if (data.middle)
                    parentNode.x = 0
                else if (!nodeData || nodeData.middle)
                    parentNode.x = nodeWidth / 2 + parentData.width / 2 - spouseWidth + chart_util_1.H_SPACING / 2
                else if (data.left)
                    parentNode.x = nodeX + d3_array_1.min([nodeWidth / 2 - parentData.width / 2, middleX])
                else
                    parentNode.x = nodeX + d3_array_1.max([
                        parentData.width / 2 - nodeWidth / 2 + indiWidth / 2 + chart_util_1.H_SPACING,
                        middleX,
                    ])
            }
        })
        return Array.from(ancestorData.values())
            .map(function (data) { return data.descendantNodes })
            .reduce(function (a, b) { return a.concat(b) }, [])
    }

    RelativesChart.prototype.render = function () {
        const descendantNodes = descendant_chart_1.layOutDescendants(this.options)
        // Don't use common id generator because these nodes will not be drawn
        const ancestorOptions = Object.assign({}, this.options, { idGenerator: undefined })
        const ancestorsRoot = ancestor_chart_1.getAncestorsTree(ancestorOptions)
        const ancestorDescendants = this.layOutAncestorDescendants(ancestorsRoot, descendantNodes[0])
        const nodes = descendantNodes.concat(ancestorDescendants)
        const animationPromise = this.util.renderChart(nodes)
        const info = chart_util_1.getChartInfo(nodes)
        this.util.updateSvgDimensions(info)
        return Object.assign(info, { animationPromise: animationPromise })
    }
    return RelativesChart
}())
exports.RelativesChart = RelativesChart